<template>
  <div class="login-form">
    <h2 class="text-campton-bold">{{ $gettext('Sign in') }}</h2>
    <b-form @submit.prevent="login" class="gk-clear-form">
      <b-form-group id="fieldset-login-email" class="form-group floating-label">
        <b-form-input 
          type="email"
          id="input-login-email"
          placeholder=" "
          class="floating-label"
          v-model="email"
          trim required
        />
        <label class="floating-label">{{ $gettext('Email address') }}</label>
      </b-form-group>
      <b-form-group id="fieldset-login-password" class="form-group floating-label">
        <b-form-input 
          type="password"
          id="input-login-password"
          placeholder=" "
          class="floating-label"
          v-model="password"
          trim required
        />
        <label class="floating-label">{{ $gettext('Password') }}</label>
        <b-form-text class="text-right">
          <GekkoButton
            @click="showForgotPassword"
            :text="$gettext('Forgot your password?')"
            variant="link"
            class="text-campton-book p-0 text-blue"
            size="sm"
          />
        </b-form-text>
      </b-form-group>

      <p v-if="showInvalidGrantError" class="text-danger text-center">
        <font-awesome-icon icon="fa-regular fa-warning" />
        {{ invalidGrantErrorTxt }}
      </p>
      <div v-if="accessGranted">
        <p class="text-primary text-center">
          {{ $gettext('Login successful.') }}
        </p>
      </div>
      <div style="padding-top: 0.5rem; width: 100%;">
        <GekkoButton
          type="submit"
          :text="$gettext('Sign in')"
          :loading="loading"
          variant="primary"
          size="xl"
          class="text-campton-book position-relative w-100"
        />
      </div>

      <div class="gk-divider">
        <div class="divider-line"></div>
        <div class="divider-text">{{ $gettext('or') }}</div>
        <div class="divider-line"></div>
      </div>

      <GoogleSSOButton 
        ref="googleSSOButton"
        @country-not-set="$emit('show-country', false)" 
        @authenticatedWithGoogle="handleThirdPartyAuth"
        :businesslocation="businesslocation" 
        :preferredlanguage="preferredlanguage"
      />
      <AppleSSOButton 
        ref="appleSSOButton"
        @country-not-set="$emit('show-country', false)" 
        @authenticatedWithApple="handleThirdPartyAuth"
        :businesslocation="businesslocation" 
        :preferredlanguage="preferredlanguage"
      />
    </b-form>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex"

import { randomCelebName, randomEmail } from "@/utils/"
import AppleSSOButton from "@/components/auth/AppleSSOButton.vue"
import GoogleSSOButton from "@/components/auth/GoogleSSOButton.vue"
import GekkoButton from "@/components/GekkoButton"
import DashboardService from "@/store/services/dashboard.service"

export default {
  name: 'LoginForm',
  components: {
    GekkoButton,
    GoogleSSOButton,
    AppleSSOButton
  },
  data () {
    return {
      email: '',
      password: '',
      businesslocation: null,
      preferredlanguage: null,
      showInvalidGrantError: false,
      invalidGrantErrorTxt: '',
      accessGranted: false,
      loading: false
    }
  },
  computed: {
    ...mapGetters({
      countryOptions: 'main/getCountryOptions',
      supportedCountries: 'signup/getSupportedCountries',
      userCountry: 'signup/getUserCountry'
    }),
    randomPlaceholder () {
      return randomEmail(randomCelebName())
    },
    formCountryOptions () {
      const extracted = {}
      const remaining = {}
      for (const key in this.countryOptions) {
        if (this.supportedCountries.includes(key)) {
          extracted[key] = this.countryOptions[key]
        } else {
          remaining[key] = this.countryOptions[key]
        }
      }
      return [
        { label: this.$gettext('Supported countries'), options: extracted },
        { label: this.$gettext('Others'), options: remaining }
      ]
    }
  },
  created () {

  },
  mounted () {
  },
  methods: {
    ...mapActions({ 
      getAccessToken: "auth/getAccessToken",
    }),
    ...mapMutations({
      setNewSignup: 'signup/SET_NEW_SIGNUP'
    }),
    login () {
      this.loading = true

      this.getAccessToken({
        'username': this.email,
        'password': this.password,
      }).then((response) => {
        console.log('Login.vue::login() -> getAccessToken THEN response', response)
        this.grantAccess()
      }).catch((err) => {
        console.log('getAccessToken ERROR response.data', err)
        if (err.data.error === 'invalid_grant') {
          this.showInvalidGrantError = true
          this.invalidGrantErrorTxt = err.data.error_description + ' ' + this.$gettext('Both e-mail and password are case-sensitive.')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    showForgotPassword () {
      this.$emit('remember-email', this.email)
      this.$router.push({ name: 'ForgotPassword' })
    },
    handleThirdPartyAuth(is_authenticated) {
      this.loading = false
      if (is_authenticated) {
        this.grantAccess()
      } else {
        this.showInvalidGrantError = true
        this.invalidGrantErrorTxt = this.$gettext('Third party authentication failed.')
      }
    },
    grantAccess () {
      this.showInvalidGrantError = false
      this.accessGranted = true

      // For users who get the delete account link via e-mail, but aren't logged in
      if (this.$route.query.delete_account === 'true' && this.$route.query.token) {
        DashboardService.accessTokenDeleteProfile({'delete_token': this.$route.query.token}).then((response) => {
          if (response.status === 204) {
            window.location.href = window.Urls['gekko_dashboard_delete'](this.$route.query.token)
          }
        }).catch((error) => {
            let msg = ''
            if (error.status == 404) {
                msg = this.$gettext('We could not find this token. Try copy/pasting the exact link.')
            } else if (error.status == 403) {
                msg = this.$gettext('You are logged in to the wrong account. Please log in with the account for which this link was requested.')
            } else {
                msg = this.$gettext('Unkown error')
            }
            this.$store.commit('setAlert', { title: this.$gettext('Error deleting account'), text: msg, variant: 'danger', duration: 4 })
        })
        return
      }

      this.$emit('authorised', false)
    },
  }
}
</script>

<style lang="scss" scoped>

</style>